import '@/styles/app.scss';

import { PaletteMode } from '@mui/material';

// ------------------ From styles/global/_evx-variables.scss ---------------
const white = '#e9eaf4';
const transparent = 'rgba(0,0,0,0)';
const martiniqueBlueDark = '#272344';
const martiniqueBlue = '#2b274b';
const martiniqueBlueLight = '#403d5d';
const martiniqueBlueLighter = '#55526f';
const martiniqueBlueLightest = '#aaa9b7';
const dodgerBlueDarker = '#2453ad';
const dodgerBlue = '#3477F7';
const dodgerBlueLighter = '#71a0f9';
const radicalRedDarkest = '#913151';
const radicalRedDarker = '#c73b4c';
const radicalRed = '#f94a5f';
const radicalRedLighter = '#fb808f';

// ------------------------------------- Widget --------------------------------
const evxWidgetBorders = `${martiniqueBlueLightest} 1px solid`;
const evxWidgetTextColor = white;

// ------------------------------------ Buttons --------------------------------
const defaultTheme = {
  typography: {
    fontFamily: '"Play", sans-serif !important',
  },
  components: {
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: 'inherit',
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: '0px',
          marginRight: '0px',
          marginBottom: '0px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: martiniqueBlueLight,
        },
        light: {
          borderColor: martiniqueBlueLight,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: martiniqueBlueDark,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: evxWidgetBorders,
          backgroundColor: transparent,
          color: evxWidgetTextColor,
        },
        rounded: {
          borderRadius: '4px',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          fontSize: evxWidgetBorders,
        },
        expandIcon: {
          color: white,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: white,
        },
        colorSecondary: {
          '&.Mui-checked': {
            color: white,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          color: white,
          fontSize: 16,
          '&:hover': {
            backgroundColor: transparent,
          },
        },
        icon: {
          color: white,
        },
        select: {
          padding: '8px 12px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          '& ul': {
            paddingLeft: '12px',
            marginBottom: '0px',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: martiniqueBlueLighter,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          fontSize: 16,
          '&:hover': {
            backgroundColor: martiniqueBlueLight,
          },
          '&.Mui-selected': {
            backgroundColor: martiniqueBlueLight,
            '&:hover': {
              backgroundColor: `${martiniqueBlueLight} !important`,
            },
          },
        },
        button: {
          '&:hover': {
            backgroundColor: martiniqueBlueLight,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          color: white,
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        getRowClassName: (params: { indexRelativeToCurrentPage: number }) => {
          if (params.indexRelativeToCurrentPage % 2 === 0) return ' even';
          return ' odd';
        },
      },
      styleOverrides: {
        root: {
          backgroundColor: '#1A172D',
        },
        paper: {
          minWidth: 'unset',
          width: 'fit-content',
        },
        filterForm: {
          backgroundColor: martiniqueBlue,
          gap: '20px',
          padding: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
          width: 'fit-content',
        },
        columnHeaderTitle: {
          textTransform: 'uppercase',
          whiteSpace: 'normal',
          lineHeight: '1',
          fontWeight: 'bold',
          fontSize: '12px',
        },
        /* overlay: {
          backgroundColor: transparent,
        },
        virtualScroller: {
          backgroundColor: '#1A172D',
        }, */
        columnHeader: {
          '&.energy-charged-header': {
            '& .MuiDataGrid-columnHeaderTitle': {
              textTransform: 'none',
            },
          },
          '&:focus-within': {
            outline: 'none',
          },
        },
        columnHeaders: {
          backgroundColor: martiniqueBlue,
          border: 'none',
          '&:focus-within': {
            outline: 'none',
          },
        },
        footerContainer: {
          backgroundColor: martiniqueBlue,
          // justifyContent: 'flex-start',
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            marginBottom: 0,
          },
        },
        cell: {
          borderBottom: 'none',
          padding: '0px 16px',
          '&:focus-within': {
            outline: 'none',
          },
        },
        row: {
          fontSize: '12px',
          '&.even': {
            backgroundColor: martiniqueBlueLighter,
          },
          '&.odd': {
            backgroundColor: martiniqueBlueLight,
          },
          '&:hover': {
            backgroundColor: '#363252',
          },
          '&.Mui-selected': {
            backgroundColor: '#2d275c',
            '&:hover': {
              backgroundColor: '#241f4a',
            },
          },
        },
        columnHeaderCheckbox: {
          padding: 0,
        },
        cellCheckbox: {
          padding: 0,
        },
        checkboxInput: {
          '&.Mui-checked': {
            color: dodgerBlueDarker,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 16,
          '&:hover': {
            backgroundColor: martiniqueBlueLight,
          },
          '&.Mui-selected': {
            backgroundColor: martiniqueBlueLight,
            '&:hover': {
              backgroundColor: `${martiniqueBlueLight} !important`,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              border: `${dodgerBlue} 1px solid`,
            },
          },
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              border: evxWidgetBorders,
            },
          },
          '& .MuiOutlinedInput-adornedEnd': {
            paddingRight: '0',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: white,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        text: {
          color: white,
          '&:hover': {
            backgroundColor: 'rgba(174, 201, 252, 0.1)',
          },
        },
        textPrimary: {
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(174, 201, 252, 0.1)',
          },
        },
        contained: {
          background: `linear-gradient(45deg, ${martiniqueBlue}, ${martiniqueBlueLight})`,
          color: white,
        },
        containedPrimary: {
          background: `linear-gradient(45deg, ${dodgerBlueDarker}, ${dodgerBlue})`,
          color: white,
          '&:hover': {
            background: `linear-gradient(45deg, ${dodgerBlueDarker} -10%, ${dodgerBlue} 0%)`,
          },
          '&.Mui-disabled': {
            background: `linear-gradient(45deg, rgba(113, 160, 249, 0.5), rgba(113, 160, 249, 0.5))`,
            color: 'rgba(255, 255, 255, 0.3)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(45deg, ${radicalRedDarkest}, ${radicalRedDarker})`,
          color: white,
          '&:hover': {
            background: `linear-gradient(45deg, ${radicalRedDarkest} -10%, ${radicalRedDarker} 0%)`,
          },
          '&.Mui-disabled': {
            background: `linear-gradient(45deg, rgba(251, 128, 143, 0.5), rgba(251, 128, 143, 0.5))`,
            color: 'rgba(255, 255, 255, 0.3)',
          },
        },
        containedError: {
          background: `linear-gradient(45deg, ${radicalRedDarkest}, ${radicalRedDarker})`,
          color: white,
          '&:hover': {
            background: `linear-gradient(45deg, ${radicalRedDarkest} -10%, ${radicalRedDarker} 0%)`,
          },
          '&.Mui-disabled': {
            background: `linear-gradient(45deg, rgba(251, 128, 143, 0.5), rgba(251, 128, 143, 0.5))`,
            color: 'rgba(255, 255, 255, 0.3)',
          },
        },
        outlined: {
          color: 'white',
          border: `1px solid rgba(255, 255, 255, 0.23) !important`,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
            border: `1px solid rgba(255, 255, 255, 0.23)`,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          color: white,
          fontSize: 16,
          '&:underline:before': {
            borderBottomColor: `${white} !important`,
          },
        },
        input: {
          '&.Mui-disabled': {
            color: 'gray',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          background: martiniqueBlue,
        },
      },
    },
    MuiCalendarPicker: {
      styleOverrides: {
        root: {
          background: martiniqueBlueLight,
          '& .MuiButtonBase-root.MuiPickersDay-root': {
            backgroundColor: transparent,
          },
          '& .Mui-selected ': {
            backgroundColor: `${dodgerBlue} !important`,
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark' as PaletteMode,
    primary: {
      light: dodgerBlueLighter,
      main: dodgerBlue,
      dark: dodgerBlueDarker,
      contrastText: white,
    },
    secondary: {
      light: radicalRedLighter,
      main: radicalRed,
      dark: radicalRedDarker,
      contrastText: white,
    },
  },
  status: {
    danger: 'orange',
  },
};

export default defaultTheme;
